/**
 * Alert Service - Handles all alert-related operations in Firestore
 */

import { db } from '../firebase';
import { handleFirestoreError } from '../utils';

/**
 * Retrieves all alerts for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array>} Array of alert objects with IDs
 */
export async function getAlerts(userID) {
	try {
		let alerts = [];

		const snapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Alerts")
			.get()
			.catch((e) => {
				throw e;
			});

		snapshot.docs.map((alertDoc) => {
			let alert = alertDoc.data();
			alert.ID = alertDoc.id;
			alerts.push(alert);
			return alert;
		});

		return alerts;
	} catch (error) {
		console.error("[alertService][getAlerts] - Error getting alerts:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Deletes a specific alert
 * @param {string} userID - The unique identifier for the user
 * @param {string} alertID - The ID of the alert to delete
 * @returns {Promise<void>}
 */
export async function deleteAlert(userID, alertID) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Alerts")
			.doc(alertID + "")
			.delete()
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		console.error("[alertService][deleteAlert] - Error deleting alert:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Gets custom temperature thresholds for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Object>} Temperature threshold settings
 */
export async function getCustomTemperatureThresholds(userID) {
	try {
		let thresholds;

		await db
			.collection("Users")
			.doc(userID)
			.collection("Account")
			.doc("Thresholds")
			.get()
			.then((documentSnapshot) => {
				if (documentSnapshot.exists) {
					thresholds = documentSnapshot.data();
				} else {
					thresholds = {};
				}
			})
			.catch((e) => {
				throw e;
			});

		return thresholds;
	} catch (error) {
		console.error("[alertService][getCustomTemperatureThresholds] - Error getting temperature thresholds:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Sets custom temperature thresholds for a user
 * @param {string} userID - The unique identifier for the user
 * @param {Object} threshold - Temperature threshold settings to save
 * @returns {Promise<void>}
 */
export async function setCustomTemperatureThresholds(userID, threshold) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Account")
			.doc("Thresholds")
			.set(threshold, { merge: true })
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		console.error("[alertService][setCustomTemperatureThresholds] - Error setting temperature thresholds:", error);
		handleFirestoreError(error);
		throw error;
	}
}

